import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  AFFILIATE_LINK_CLASSNAME,
  AFFILIATE_TOOLTIP,
  appendAffiliateTag,
  buildAmazonAffiliateUrl,
  isbnToAsin,
} from "@/lib/amazonAffiliate";
import {
  DEFAULT_CITATION_STYLE,
  resolveCitationStyle,
  resolveCslStyleId,
  type CitationStyle,
} from "@/lib/citationStyles";
import type { LibraryItem } from "@/lib/library";
import type {
  ContentBriefWithReferences,
  ResolvedBriefReference,
} from "@/lib/composerCore";
import type { BriefCitationMode } from "@/lib/composerCore";
import { readBriefCitationStyleOverride } from "@/lib/composer/core/pieces/briefs";

export type EnrichResult = {
  asin: string;
  status: "found" | "not_found" | "error";
  title?: string;
  detailPageUrl?: string;
};

const STYLE_ID_RE = /^[a-z0-9][a-z0-9-]{0,127}$/i;
export const isCitationStyle = (v: string | null | undefined): v is CitationStyle =>
  !!v && STYLE_ID_RE.test(v);

export const readCitationModeFromMetadata = (
  metadata: ContentBriefWithReferences["metadata"],
): BriefCitationMode => {
  if (metadata && typeof metadata === "object" && !Array.isArray(metadata)) {
    const raw = (metadata as Record<string, unknown>).citationMode;
    if (raw === "endnotes") return "endnotes";
  }
  return "footnotes";
};

export const sourceReferences = (refs: ResolvedBriefReference[]) =>
  refs.filter((r) => r.reference_type === "source");

/**
 * Wrap the first italic title fragment (`<i>…</i>`) in the rendered citation
 * HTML with an affiliate-tagged anchor. Inherits surrounding theme link color.
 */
export const wrapTitleWithAffiliateLink = (html: string, href: string): string => {
  // `renderCitation` emits italic titles as <em>…</em>; older code paths may
  // use <i>…</i>. Match either, and preserve the original tag inside the
  // anchor so the visual italic is identical to the un-linked version.
  const m = /<(em|i)>([\s\S]*?)<\/\1>/.exec(html);
  if (!m) return html;
  const tag = m[1];
  const inner = m[2];
  const safeHref = href.replace(/"/g, "&quot;");
  const anchor =
    `<a href="${safeHref}" target="_blank" rel="nofollow sponsored noopener noreferrer" ` +
    `title="${AFFILIATE_TOOLTIP}" aria-label="${AFFILIATE_TOOLTIP}" ` +
    `class="${AFFILIATE_LINK_CLASSNAME}"><${tag}>${inner}</${tag}></a>`;
  return html.slice(0, m.index) + anchor + html.slice(m.index + m[0].length);
};

export type CitationsPreviewData = ReturnType<typeof useCitationsPreviewData>;

export function useCitationsPreviewData(
  workspaceId: string,
  piece: ContentBriefWithReferences,
) {
  const sourceRefs = useMemo(() => sourceReferences(piece.references), [piece.references]);
  const sourceIds = useMemo(
    () => Array.from(new Set(sourceRefs.map((r) => r.reference_id))),
    [sourceRefs],
  );

  const [style, setStyle] = useState<CitationStyle>(DEFAULT_CITATION_STYLE);
  const [items, setItems] = useState<Map<string, LibraryItem>>(new Map());
  const [loading, setLoading] = useState(false);
  const [associatesTag, setAssociatesTag] = useState<string | null>(null);
  const [disclosureText, setDisclosureText] = useState<string | null>(null);
  const [paapiConnected, setPaapiConnected] = useState(false);
  const [enrichment, setEnrichment] = useState<Map<string, EnrichResult>>(new Map());

  // Workspace defaults: citation style + Amazon affiliate config + PA-API status.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [{ data: profile }, { data: paapi }] = await Promise.all([
        supabase
          .from("profiles")
          .select("default_citation_style, amazon_associates_tag, amazon_disclosure_text")
          .eq("id", workspaceId)
          .maybeSingle(),
        supabase
          .from("workspace_amazon_paapi_credentials")
          .select("status")
          .eq("workspace_id", workspaceId)
          .maybeSingle(),
      ]);
      if (cancelled) return;
      const row = profile as {
        default_citation_style?: string;
        amazon_associates_tag?: string | null;
        amazon_disclosure_text?: string | null;
      } | null;
      const wsDefault = isCitationStyle(row?.default_citation_style)
        ? resolveCslStyleId(row!.default_citation_style!)
        : null;
      const override = readBriefCitationStyleOverride(piece.metadata);
      setStyle(resolveCitationStyle(wsDefault, override));
      setAssociatesTag(row?.amazon_associates_tag ?? null);
      setDisclosureText(row?.amazon_disclosure_text ?? null);
      setPaapiConnected((paapi as { status?: string } | null)?.status === "connected");
    })();
    return () => { cancelled = true; };
  }, [workspaceId, piece.metadata]);

  // library_items for source references.
  useEffect(() => {
    let cancelled = false;
    if (sourceIds.length === 0) {
      setItems(new Map());
      return;
    }
    setLoading(true);
    void (async () => {
      const { data, error } = await supabase
        .from("library_items")
        .select("*")
        .in("id", sourceIds);
      if (cancelled) return;
      if (error) {
        toast.error(error.message);
        setItems(new Map());
      } else {
        const map = new Map<string, LibraryItem>();
        ((data ?? []) as LibraryItem[]).forEach((row) => map.set(row.id, row));
        setItems(map);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [sourceIds]);

  // PA-API enrichment when connected.
  useEffect(() => {
    if (!paapiConnected || items.size === 0) {
      setEnrichment(new Map());
      return;
    }
    const asins = Array.from(
      new Set(
        [...items.values()]
          .filter((i) => i.source_type === "book")
          .map((i) => isbnToAsin(i.isbn_13 ?? i.isbn_10 ?? null))
          .filter((a): a is string => !!a),
      ),
    );
    if (asins.length === 0) {
      setEnrichment(new Map());
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase.functions.invoke("amazon-paapi-enrich", {
        body: { workspaceId, asins },
      });
      if (cancelled || error) return;
      const results = (data as { results?: EnrichResult[] } | null)?.results ?? [];
      const map = new Map<string, EnrichResult>();
      for (const r of results) map.set(r.asin, r);
      setEnrichment(map);
    })();
    return () => { cancelled = true; };
  }, [paapiConnected, items, workspaceId]);

  const cited = useMemo(
    () => sourceRefs.filter((r) => r.cited !== false),
    [sourceRefs],
  );
  const consulted = useMemo(
    () => sourceRefs.filter((r) => r.cited === false),
    [sourceRefs],
  );

  const affiliateHrefFor = (item: LibraryItem): string | null => {
    if (!associatesTag || item.source_type !== "book") return null;
    const isbn = item.isbn_13 ?? item.isbn_10 ?? null;
    // Prefer a PA-API-confirmed DetailPageURL when available; otherwise fall
    // back to the plain `/dp/{ASIN}?tag=…` link built from the ISBN. This
    // makes affiliate linking work for any workspace that has set an
    // Associates tag, with or without PA-API credentials.
    const asin = isbnToAsin(isbn);
    if (asin) {
      const hit = enrichment.get(asin);
      if (hit && hit.status === "found" && hit.detailPageUrl) {
        return appendAffiliateTag(hit.detailPageUrl, associatesTag);
      }
    }
    return buildAmazonAffiliateUrl({ isbn, associatesTag });
  };

  return {
    sourceRefs, style, items, loading, associatesTag, disclosureText,
    paapiConnected, enrichment, cited, consulted, affiliateHrefFor,
  };
}
